import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiSuccessResponse } from '../types/api.types';

// Read backend URL from Vite env; fall back to same-origin (proxy mode in dev)
// Normalizes trailing slashes and avoids duplicate /api suffixes
const rawBaseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.trim() ?? '';
const normalizedBaseUrl = rawBaseUrl.replace(/\/+$/, '').replace(/\/api$/, '');
const API_BASE_URL = normalizedBaseUrl ? `${normalizedBaseUrl}/api` : '/api';

// ── Token storage helpers ────────────────────────────────────
const TOKEN_KEY = 'ielts_auth_token';

export const tokenStorage = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  remove: (): void => localStorage.removeItem(TOKEN_KEY),
};

// ── Axios instance ───────────────────────────────────────────
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ── Request interceptor — attach Bearer token ────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = tokenStorage.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ── Response interceptor — handle 401 globally ───────────────
// We use a callback instead of importing AuthContext directly
// to avoid a circular dependency.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedCallback(cb: () => void): void {
  onUnauthorized = cb;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        tokenStorage.remove();
        onUnauthorized?.();
      } else {
        console.error('[API Error]', error.response?.data ?? error.message);
      }
    }
    return Promise.reject(error);
  },
);

// ── Typed helpers ────────────────────────────────────────────

function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
}

export async function apiGet<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  try {
    const response = await apiClient.get<ApiResponse<T>>(path, { params });
    const body = response.data;
    if (body.success) return (body as ApiSuccessResponse<T>).data;
    throw new Error(body.message);
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export async function apiPost<T, D = unknown>(path: string, data: D): Promise<T> {
  try {
    const response = await apiClient.post<ApiResponse<T>>(path, data);
    const body = response.data;
    if (body.success) return (body as ApiSuccessResponse<T>).data;
    throw new Error(body.message);
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export async function apiPut<T, D = unknown>(path: string, data: D): Promise<T> {
  try {
    const response = await apiClient.put<ApiResponse<T>>(path, data);
    const body = response.data;
    if (body.success) return (body as ApiSuccessResponse<T>).data;
    throw new Error(body.message);
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export async function apiDelete<T>(path: string): Promise<T> {
  try {
    const response = await apiClient.delete<ApiResponse<T>>(path);
    const body = response.data;
    if (body.success) return (body as ApiSuccessResponse<T>).data;
    throw new Error(body.message);
  } catch (err) {
    throw new Error(extractErrorMessage(err));
  }
}

export default apiClient;
